import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "../../components/alert";
import Button from "../../components/button";
import { InputField, Select } from "../../components/input-functions";
import Modal from "../../components/modal";
import MultiSelect from "../../components/searchable-multi-select";
import {
  AuthzPolicy,
  AuthzRole,
  AuthzUserProfile,
} from "../../actions/authz.action";

interface RoleChangeConfirmModalProps {
  open: boolean;
  profile: AuthzUserProfile | null;
  previousRoleName: string;
  nextRoleName: string;
  submitting: boolean;
  submitError?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RoleChangeConfirmModal({
  open,
  profile,
  previousRoleName,
  nextRoleName,
  submitting,
  submitError,
  onConfirm,
  onCancel,
}: RoleChangeConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Change user's role?"
      hasSubmitButton={false}
      hasCancelButton={false}
      closeOnBackdropClick={false}
      closeOnEscPress={false}
    >
      <p>
        Change <strong>{profile?.fullName}</strong> ({profile?.email}) role from{" "}
        <strong>{previousRoleName || "—"}</strong> to{" "}
        <strong>{nextRoleName}</strong>?
      </p>
      <p className="text-muted" style={{ fontSize: "0.9em" }}>
        The user is assigned a single role. Their permissions will update as
        soon as this change is applied.
      </p>
      {submitError && <Alert type="danger" message={submitError} canDismiss />}
      <div className="d-flex justify-content-end mt-3">
        <Button variant="text" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={submitting}
          disabled={submitting}
          onClick={onConfirm}
        >
          Change Role
        </Button>
      </div>
    </Modal>
  );
}

interface RemoveUserModalProps {
  open: boolean;
  profile: AuthzUserProfile | null;
  submitting: boolean;
  submitError?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RemoveUserModal({
  open,
  profile,
  submitting,
  submitError,
  onConfirm,
  onCancel,
}: RemoveUserModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [typedEmail, setTypedEmail] = useState("");

  function resetAndClose() {
    setStep(1);
    setTypedEmail("");
    onCancel();
  }

  const emailToMatch = profile?.email?.toLowerCase() || "";
  const confirmEnabled =
    step === 2 && typedEmail.toLowerCase() === emailToMatch;

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      title={step === 1 ? "Remove portal user" : "Confirm removal"}
      hasSubmitButton={false}
      hasCancelButton={false}
      closeOnBackdropClick={false}
      closeOnEscPress={false}
    >
      {step === 1 ? (
        <>
          <p>
            You are about to remove <strong>{profile?.fullName}</strong> (
            {profile?.email}).
          </p>
          <ul>
            <li>The user will be deactivated immediately.</li>
            <li>
              Active sessions are terminated and access to the portal revoked.
            </li>
            <li>
              The account and its audit trail are retained for compliance
              (soft-delete, no data loss).
            </li>
          </ul>
          <div className="d-flex justify-content-end mt-3">
            <Button variant="text" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button variant="danger-fill" onClick={() => setStep(2)}>
              Continue
            </Button>
          </div>
        </>
      ) : (
        <>
          <p>
            This is a destructive action. Type <strong>{profile?.email}</strong>{" "}
            to confirm removal.
          </p>
          <InputField
            id="remove-user-email"
            label="User email"
            placeholder={profile?.email}
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
          />
          {submitError && (
            <Alert type="danger" message={submitError} canDismiss />
          )}
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="text"
              onClick={resetAndClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger-fill"
              loading={submitting}
              disabled={submitting || !confirmEnabled}
              onClick={onConfirm}
            >
              Remove User
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export type EntityEditItem =
  | { kind: "role"; create: boolean; entity?: AuthzRole }
  | { kind: "policy"; create: boolean; entity?: AuthzPolicy };

interface EntityEditorModalProps {
  open: boolean;
  item: EntityEditItem | null;
  policyOptions: Array<{ value: string; label: string }>;
  resourceOptions: Array<{ value: string; label: string }>;
  actionOptions: Array<{ value: string; label: string }>;
  effectOptions: Array<{ value: string; label: string }>;
  submitting: boolean;
  submitError?: string;
  onClose: () => void;
  onSave: (payload: {
    kind: "role" | "policy";
    _id?: string;
    name?: string;
    policies?: string[];
    resource?: string;
    effect?: string;
    action?: string;
    conditions?: unknown[];
  }) => void;
}

export function EntityEditorModal({
  open,
  item,
  policyOptions,
  resourceOptions,
  actionOptions,
  effectOptions,
  submitting,
  submitError,
  onClose,
  onSave,
}: EntityEditorModalProps) {
  const [name, setName] = useState("");
  const [policies, setPolicies] = useState<string[]>([]);
  const [resource, setResource] = useState("");
  const [effect, setEffect] = useState("");
  const [action, setAction] = useState("");
  const [conditionsText, setConditionsText] = useState("[]");
  const [error, setError] = useState("");

  const isRole = item?.kind === "role";

  const policyIdSet = useMemo(
    () => new Set(policyOptions.map((o) => o.value)),
    [policyOptions],
  );

  const openModal = useCallback(() => {
    setName(item?.kind === "role" ? item.entity?.name || "" : "");
    const seededPolicies =
      item?.kind === "role" ? item.entity?.policies || [] : [];
    const stalePolicies = seededPolicies.filter((p) => !policyIdSet.has(p));
    setPolicies(seededPolicies.filter((p) => policyIdSet.has(p)));
    setResource(item?.kind === "policy" ? item.entity?.resource || "" : "");
    setEffect(item?.kind === "policy" ? item.entity?.effect || "" : "");
    setAction(item?.kind === "policy" ? item.entity?.action || "" : "");
    setConditionsText(
      item?.kind === "policy"
        ? JSON.stringify(item.entity?.conditions || [], null, 2)
        : "[]",
    );
    setError(
      stalePolicies.length > 0
        ? `Ignored ${stalePolicies.length} policy reference(s) that no longer exist: ${stalePolicies.join(", ")}.`
        : "",
    );
  }, [item, policyIdSet]);

  // Seed the local state each time the modal opens with a different item.
  const [lastItemKey, setLastItemKey] = useState("");
  const itemKey = item ? `${item.kind}-${item.entity?._id || "new"}` : "";

  useEffect(() => {
    if (open && itemKey !== lastItemKey) {
      setLastItemKey(itemKey);
      openModal();
    }
  }, [open, itemKey, lastItemKey, openModal]);

  function handleSave() {
    setError("");
    if (isRole) {
      if (!name.trim()) {
        setError("Role name is required");
        return;
      }
      onSave({
        kind: "role",
        _id: item?.entity?._id,
        name: name.trim(),
        policies,
      });
      return;
    }
    if (!resource.trim() || !effect || !action.trim()) {
      setError("Resource, effect and action are required");
      return;
    }
    let conditions: unknown[] = [];
    try {
      conditions = JSON.parse(conditionsText.trim() || "[]");
    } catch {
      setError("Conditions must be valid JSON");
      return;
    }
    onSave({
      kind: "policy",
      _id: item?.entity?._id,
      resource: resource.trim(),
      effect,
      action: action.trim(),
      conditions,
    });
  }

  const title = `${item?.create ? "Create" : "Edit"} ${
    isRole ? "Role" : "Policy"
  }`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      hasSubmitButton={false}
      hasCancelButton={false}
      closeOnBackdropClick={false}
      closeOnEscPress={false}
    >
      {isRole ? (
        <>
          <div className="form-label-group my-2">
            <label htmlFor="role-name" style={{ color: "#495057" }}>
              Name <span className="rf-input-star">*</span>
            </label>
            <input
              id="role-name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. relationship_manager"
            />
          </div>
          <div className="mt-3">
            <MultiSelect
              options={policyOptions.map((o) => ({
                value: o.value,
                text: o.label,
              }))}
              placeholder="Search and select policies"
              search
              selectAll
              showBadges
              clearable
              value={policies}
              onChange={(selected) =>
                setPolicies(Array.from(new Set(selected.map(String))))
              }
            />
          </div>
        </>
      ) : (
        <>
          <div className="form-label-group my-2">
            <label htmlFor="policy-resource" style={{ color: "#495057" }}>
              Resource <span className="rf-input-star">*</span>
            </label>
            <Select
              id="policy-resource"
              className="form-control"
              placeholder="Select resource"
              options={resourceOptions}
              value={resource}
              onChange={(e) => setResource(e.target.value)}
            />
          </div>
          <div className="form-label-group my-2">
            <label htmlFor="policy-effect" style={{ color: "#495057" }}>
              Effect <span className="rf-input-star">*</span>
            </label>
            <Select
              id="policy-effect"
              className="form-control"
              placeholder="Select effect"
              options={effectOptions}
              value={effect}
              onChange={(e) => setEffect(e.target.value)}
            />
          </div>
          <div className="form-label-group my-2">
            <label htmlFor="policy-action" style={{ color: "#495057" }}>
              Action <span className="rf-input-star">*</span>
            </label>
            <Select
              id="policy-action"
              className="form-control"
              placeholder="Select action"
              options={actionOptions}
              value={action}
              onChange={(e) => setAction(e.target.value)}
            />
          </div>
          <div className="form-label-group my-2">
            <label htmlFor="policy-conditions" style={{ color: "#495057" }}>
              Conditions (JSON)
            </label>
            <textarea
              id="policy-conditions"
              className="form-control"
              rows={4}
              value={conditionsText}
              onChange={(e) => setConditionsText(e.target.value)}
              placeholder="[]"
            />
          </div>
        </>
      )}
      {(error || submitError) && (
        <Alert type="danger" message={error || submitError} canDismiss />
      )}
      <div className="d-flex justify-content-end mt-3">
        <Button variant="text" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={submitting}
          disabled={submitting}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}
